import { ReactNode, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { AuroraBG } from '../ui/AuroraBG';
import { GoogleAdsBanner } from '../ui/GoogleAdsBanner';
import { CommandPalette } from '../ui/CommandPalette';
import { NotificationCenter } from '../ui/NotificationCenter';
import { ChatWidget } from '../chat/ChatWidget';
import { AIOpsCopilotPanel } from '../ai/AIOpsCopilotPanel';
import { LanguageSelector } from '../ui/LanguageSelector';
import { AppIcon, type AppIconName } from '../ui/AppIcon';
import {
  ChefHat,
  LayoutDashboard,
  Package,
  Upload,
  TrendingUp,
  Settings,
  Shield,
  LogOut,
  Database,
  Brain,
  Trash2,
  FileText,
  ShoppingCart,
  Utensils,
  Warehouse,
  ArrowRightLeft,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Users,
  Bell,
  HelpCircle,
  Search,
  FolderTree,
  MessageSquare,
  DollarSign,
  CreditCard,
  Building2,
  Mail,
  UserPlus,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { clsx } from 'clsx';

interface DashboardLayoutProps {
  children: ReactNode;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon | AppIconName;
  badge?: string;
  badgeColor?: string;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { t } = useTranslation();
  const { signOut, user, isSuperAdmin, currentTenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    core: true,
    procurement: true,
    warehouses: true,
    'ai features': true,
    finance: true,
    system: true,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCopilot, setShowCopilot] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette((prev) => !prev);
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'j') {
        e.preventDefault();
        setShowCopilot((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navigationSections: NavSection[] = [
    {
      title: 'CORE',
      items: [
        { name: t('common:navigation.dashboard'), href: '/dashboard', icon: LayoutDashboard },
        { name: t('common:navigation.categories'), href: '/categories', icon: FolderTree },
        { name: t('common:navigation.items'), href: '/items', icon: Package },
        { name: t('common:navigation.salesImport'), href: '/imports', icon: Upload },
        { name: t('common:navigation.consumption'), href: '/consumption', icon: TrendingUp },
      ],
    },
    {
      title: 'PROCUREMENT',
      items: [
        { name: t('common:navigation.procurementHub'), href: '/procurement', icon: ShoppingCart },
        { name: t('common:navigation.suppliers'), href: '/procurement/suppliers', icon: Users },
        { name: t('common:navigation.purchaseInvoices'), href: '/procurement/invoices', icon: FileText },
        { name: 'Supplier Contracts', href: '/supplier-contracts', icon: FileText },
        { name: t('common:navigation.receivingQueue'), href: '/receiving', icon: Package },
        { name: 'MyData Sync', href: '/mydata-sync', icon: Database, badge: 'SYNC' },
      ],
    },
    {
      title: 'WAREHOUSES',
      items: [
        { name: 'POS Warehouse Mapping', href: '/pos-warehouse', icon: Warehouse },
        { name: 'Transfer Requests', href: '/transfer-requests', icon: ArrowRightLeft },
        { name: 'Warehouse Analytics', href: '/warehouse-analytics', icon: BarChart3 },
        { name: 'Warehouse Rounds', href: '/warehouse-rounds', icon: Package },
      ],
    },
    {
      title: 'AI FEATURES',
      items: [
        { name: 'AI Copilot', href: '/ai-copilot', icon: Sparkles, badge: 'NEW' },
        { name: 'AI Dashboard', href: '/ai-dashboard', icon: Brain, badge: 'AI' },
        { name: 'Menu Import', href: '/menu-import', icon: Utensils, badge: 'AI' },
        { name: 'Recipe Review', href: '/recipe-review', icon: Brain, badge: 'AI' },
        { name: 'Predictive Inventory', href: '/predictive-inventory', icon: Brain, badge: 'AI' },
        { name: 'Waste Reduction', href: '/waste-reduction', icon: Trash2, badge: 'AI' },
        { name: 'Cost Analytics', href: '/procurement/cost-analytics', icon: BarChart3 },
        { name: 'Channel Optimizer', href: '/channel-optimizer', icon: TrendingUp, badge: 'AI' },
      ],
    },
    {
      title: 'FINANCE',
      items: [
        { name: 'Treasury Dashboard', href: '/treasury', icon: DollarSign, badge: 'NEW' },
        { name: 'Bank Accounts', href: '/integrations?tab=banking', icon: Database },
        { name: 'POS Terminals', href: '/integrations?tab=pos', icon: CreditCard },
        { name: 'Supplier Payments', href: '/financials/payments', icon: FileText },
      ],
    },
    {
      title: 'SYSTEM',
      items: [
        { name: 'Internal Apps', href: '/apps', icon: 'apps', badge: 'NEW' },
        { name: 'Mobile Apps', href: '/apps?filter=mobile', icon: 'phone', badge: 'PWA' },
        { name: 'AI Agent Config', href: '/ai-agent', icon: Brain },
        { name: 'Automation', href: '/automation', icon: 'automation', badge: 'PRO' },
        { name: 'Integrations', href: '/integrations', icon: Database },
        { name: 'Help Center', href: '/faq', icon: HelpCircle },
        { name: 'FAQ Management', href: '/faq-management', icon: MessageSquare },
        { name: 'Settings', href: '/settings', icon: Settings },
      ],
    },
  ];

  const adminSection: NavSection = {
    title: 'ADMIN',
    items: [
      { name: 'Platform Dashboard', href: '/admin', icon: BarChart3, badge: 'ADMIN' },
      { name: 'Onboarding Analytics', href: '/admin/onboarding', icon: UserPlus },
      { name: 'Tenants', href: '/admin/tenants', icon: Building2 },
      { name: 'Subscriptions', href: '/admin/subscriptions', icon: CreditCard },
      { name: 'Internal Messages', href: '/admin/messages', icon: Mail },
    ],
  };

  const allNavigationSections = isSuperAdmin
    ? [...navigationSections, adminSection]
    : navigationSections;

  const toggleSection = (sectionTitle: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionTitle.toLowerCase()]: !prev[sectionTitle.toLowerCase()],
    }));
  };

  const filteredSections = searchQuery
    ? allNavigationSections.map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          item.name.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter((section) => section.items.length > 0)
    : allNavigationSections;

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <>
      <AuroraBG />
      <CommandPalette isOpen={showCommandPalette} onClose={() => setShowCommandPalette(false)} />
      <NotificationCenter isOpen={showNotifications} onClose={() => setShowNotifications(false)} />
      <AIOpsCopilotPanel
        isOpen={showCopilot}
        onClose={() => setShowCopilot(false)}
        context={{
          route: location.pathname,
          tenantId: currentTenant || '',
        }}
      />
      <div className="min-h-screen flex">
        <aside className="w-72 glass-panel m-4 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-6 px-2">
            <div className="flex items-center">
              <ChefHat className="w-8 h-8 text-primary-400 mr-2" />
              <h1 className="text-xl font-bold text-white">{t('common:appName')}</h1>
            </div>
            <div className="flex gap-1">
              <LanguageSelector />
              <button
                onClick={() => setShowCopilot(!showCopilot)}
                className="relative p-2 rounded-lg hover:bg-white/5 transition-colors group"
                title="AI Copilot (⌘⇧J)"
              >
                <Sparkles className={clsx(
                  "w-5 h-5 transition-colors",
                  showCopilot ? "text-primary-400" : "text-white/60 group-hover:text-white"
                )} />
              </button>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-lg hover:bg-white/5 transition-colors group"
                title={t('common:common.notifications')}
              >
                <Bell className="w-5 h-5 text-white/60 group-hover:text-white" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-error-400 rounded-full" />
              </button>
              <button
                className="p-2 rounded-lg hover:bg-white/5 transition-colors group"
                title={t('common:common.help')}
              >
                <HelpCircle className="w-5 h-5 text-white/60 group-hover:text-white" />
              </button>
            </div>
          </div>

          <div className="relative mb-4 px-2">
            <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('common:common.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:border-primary-400/50 transition-colors"
            />
          </div>

          <nav className="flex-1 overflow-y-auto space-y-1 px-2">
            {filteredSections.map((section) => {
              const sectionKey = section.title.toLowerCase();
              const isExpanded = expandedSections[sectionKey] !== false;

              return (
                <div key={section.title} className="mb-3">
                  <button
                    onClick={() => toggleSection(section.title)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider hover:text-white/70 transition-colors"
                  >
                    <span>{section.title}</span>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="space-y-0.5 mt-1">
                      {section.items.map((item, idx) => {
                        const isActive = location.pathname === item.href || location.pathname.startsWith(item.href.split('?')[0]);
                        const isAppIcon = typeof item.icon === 'string';
                        const ItemIcon = isAppIcon ? null : item.icon as LucideIcon;

                        return (
                          <Link
                            key={`${item.name}-${idx}`}
                            to={item.href}
                            className={clsx(
                              'flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all group',
                              isActive
                                ? 'bg-primary-500/30 text-white border border-primary-400/50 shadow-lg shadow-primary-500/20'
                                : 'text-white/70 hover:bg-white/5 hover:text-white'
                            )}
                          >
                            <div className="flex items-center">
                              {isAppIcon ? (
                                <AppIcon name={item.icon as AppIconName} size="sm" className="mr-3 flex-shrink-0" />
                              ) : ItemIcon ? (
                                <ItemIcon className="w-4 h-4 mr-3 flex-shrink-0" />
                              ) : (
                                <Database className="w-4 h-4 mr-3 flex-shrink-0" />
                              )}
                              <span className="truncate">{item.name}</span>
                            </div>
                            {item.badge && (
                              <span
                                className={clsx(
                                  'ml-2 px-1.5 py-0.5 text-xs font-bold rounded',
                                  item.badgeColor || 'bg-primary-500/80 text-white'
                                )}
                              >
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="border-t border-white/10 pt-4 mt-4 px-2">
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-primary-500/30 border border-primary-400/50 flex items-center justify-center text-white font-semibold text-sm">
                {user?.email?.[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">
                  {user?.email?.split('@')[0]}
                </div>
                <div className="text-xs text-white/50 truncate">
                  {user?.email}
                </div>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white transition-all"
            >
              <LogOut className="w-4 h-4 mr-3" />
              {t('common:actions.signOut')}
            </button>
          </div>
        </aside>

        <main className="flex-1 overflow-auto">
          <div className="p-8 max-w-[1600px] mx-auto">
            {children}
          </div>
          <GoogleAdsBanner />
        </main>
        <ChatWidget />
      </div>
    </>
  );
}
